import type { OrderDetails } from '@/entities/order/model'
import { getCollectionWindow, getOrderType } from '@/entities/order/lib'

/**
 * Gets the order type with optional collection window inline format
 * @param hsFields - The order's hsFields containing order type and collection window data
 * @returns The formatted order type with collection window if available (e.g., "Click & Collect, 11:00 - 12:00")
 */
export function getOrderTypeWithCollectionWindow(hsFields?: OrderDetails['hsFields']): string {
  const orderType = getOrderType(hsFields) || 'Standard'
  const collectionWindow = getCollectionWindow(hsFields)

  return collectionWindow ? `${orderType}, ${collectionWindow}` : orderType
}

/**
 * Determines the secondary message to display for an order status.
 * @param orderStatus - The order status to evaluate
 * @param timestamp - The formatted timestamp string to use for history/overdue orders
 * @returns The appropriate secondaryMessage value for OrderStatusDisplay component
 */
export function getOrderSecondaryMessage(
  orderStatus: string,
  timestamp?: string,
): string | undefined {
  // Only show timestamp for History and Overdue orders
  if (orderStatus === 'OrderOverdue')
    return timestamp

  // For history views - completed statuses should show timestamps
  if (['Completed', 'Fulfilled', 'Cancelled'].includes(orderStatus))
    return timestamp

  // For OnTheWay and TempoOnTheWay status, show "Prepare Order" instead of timestamp
  if (orderStatus === 'OnTheWay' || orderStatus === 'TempoOnTheWay')
    return 'Prepare Order'

  // For ReadyToPick, Arrived, Delivering - don't show anything
  return undefined
}
